"use client";

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useLanguage } from '@/context/LanguageContext';

export default function ExperiencesSection() {
  const { t } = useLanguage();

  const sections = [
    {
      id: "land",
      titleKey: "sec_land" as const,
      icon: "MapIcon",
      color: "text-amber-600",
      activities: [
        {
          id: "buggy-tours",
          nameKey: "act_buggy" as const,
          icon: "TruckIcon",
          image: "https://images.unsplash.com/photo-1671595652340-d7528f2333a3",
          alt: "Yellow dune buggy racing through golden desert sand dunes with driver in safety gear",
          descKey: "act_buggy_desc" as const,
          pricing: [{ labelKey: null, label: "1h30", price: "55€" }],
          badgeKey: "exp_popular" as const,
        },
        {
          id: "horse-riding",
          nameKey: "act_horse" as const,
          icon: "StarIcon",
          image: "https://images.unsplash.com/photo-1695697073132-8c1b310b922e",
          alt: "Rider on white Arabian horse galloping along beach at sunset with ocean waves in background",
          descKey: "act_horse_desc" as const,
          pricing: [
            { labelKey: "price_individual" as const, label: null, price: "20€" },
            { labelKey: "price_normal" as const, label: null, price: "15€" },
            { labelKey: "price_pro" as const, label: null, price: "25€" },
          ],
        },
        {
          id: "camel-tour",
          nameKey: "act_camel" as const,
          icon: "SunIcon",
          image: "https://images.unsplash.com/photo-1731355775971-222e762d8887",
          alt: "Traditional camel caravan with multiple camels and riders crossing the Sahara desert at golden hour",
          descKey: "act_camel_desc" as const,
          pricing: [{ labelKey: "price_per_person" as const, label: null, price: "18€" }],
        },
        {
          id: "quad-tours",
          nameKey: "act_quad" as const,
          icon: "BoltIcon",
          image: "https://images.unsplash.com/photo-1709830595619-1357194d1a15",
          alt: "Rider on red quad bike speeding through golden desert sand creating a large dust cloud",
          descKey: "act_quad_desc" as const,
          pricing: [
            { labelKey: null, label: "1h30", price: "15€" },
            { labelKey: null, label: "2h", price: "23€" },
            { labelKey: null, label: "3h", price: "50€" },
          ],
          badgeKey: "exp_best_value" as const,
        },
        {
          id: "caleche",
          nameKey: "act_caleche" as const,
          icon: "MapPinIcon",
          image: "https://images.unsplash.com/photo-1562311395-93638c9fff7e",
          alt: "Traditional horse-drawn carriage riding through a scenic Tunisian market street lined with palm trees",
          descKey: "act_caleche_desc" as const,
          pricing: [
            { labelKey: "price_croc" as const, label: null, price: "10€/person" },
            { labelKey: "price_lagoon" as const, label: null, price: "10€" },
            { labelKey: "price_market" as const, label: null, price: "10€" },
          ],
          notes: ["note_thu" as const, "note_fri" as const],
        },
        {
          id: "caleche-horse",
          nameKey: "act_caleche_horse" as const,
          icon: "SparklesIcon",
          image: "https://images.unsplash.com/photo-1545046957-82277bab4488",
          alt: "Horse carriage and riders on horseback together along a scenic coastal path at sunrise",
          descKey: "act_caleche_horse_desc" as const,
          pricing: [{ labelKey: "price_per_person_min2" as const, label: null, price: t("price_contact") }],
          notes: ["note_dep" as const, "note_meal" as const, "note_min2" as const],
          badgeKey: "exp_unique" as const,
        },
      ],
    },
    {
      id: "sea",
      titleKey: "sec_sea" as const,
      icon: "GlobeAltIcon",
      color: "text-blue-500",
      activities: [
        {
          id: "jet-ski",
          nameKey: "act_jetski" as const,
          icon: "BoltIcon",
          image: "https://images.unsplash.com/photo-1622885050506-71821c268895",
          alt: "Person riding a jet ski at high speed across turquoise Mediterranean sea with white water spray",
          descKey: "act_jetski_desc" as const,
          pricing: [
            { labelKey: "price_30min" as const, label: null, price: "50€/jet" },
            { labelKey: null, label: "1h30", price: "80€/person" },
          ],
          badgeKey: "exp_thrilling" as const,
        },
        {
          id: "boat-ride",
          nameKey: "act_boat" as const,
          icon: "GlobeAltIcon",
          image: "https://images.unsplash.com/photo-1522435809763-16866f0503e1",
          alt: "Scenic boat ride on calm turquoise Mediterranean waters near the coast of Djerba Tunisia",
          descKey: "act_boat_desc" as const,
          pricing: [{ labelKey: "price_per_person" as const, label: null, price: "20€" }],
        },
        {
          id: "parasailing",
          nameKey: "act_parasailing" as const,
          icon: "ArrowUpIcon",
          image: "https://images.unsplash.com/photo-1575526893705-1d8cec7b3594",
          alt: "Person parasailing high above turquoise ocean water with colorful parachute against blue sky",
          descKey: "act_parasailing_desc" as const,
          pricing: [{ labelKey: "price_per_person" as const, label: null, price: "30€" }],
          badgeKey: "exp_scenic" as const,
        },
        {
          id: "water-games",
          nameKey: "act_water" as const,
          icon: "SparklesIcon",
          image: "https://images.unsplash.com/photo-1552956012-426a4c8f2947",
          alt: "Group of people enjoying inflatable water games and banana boat rides on a sunny beach",
          descKey: "act_water_desc" as const,
          pricing: [{ labelKey: "price_per_person" as const, label: null, price: "25€" }],
        },
        {
          id: "pirate-boat",
          nameKey: "act_pirate" as const,
          icon: "StarIcon",
          image: "https://images.unsplash.com/photo-1675151583030-88d344285621",
          alt: "Colorful pirate-themed boat sailing on the Mediterranean sea with passengers enjoying the cruise",
          descKey: "act_pirate_desc" as const,
          pricing: [{ labelKey: "price_per_person" as const, label: null, price: "18.5€" }],
          badgeKey: "exp_family_fun" as const,
        },
      ],
    },
    {
      id: "excursions",
      titleKey: "sec_excursions" as const,
      icon: "MapIcon",
      color: "text-emerald-600",
      activities: [
        {
          id: "island-tour",
          nameKey: "act_island" as const,
          icon: "MapPinIcon",
          image: "https://images.unsplash.com/photo-1662877419754-27ddf7567162",
          alt: "Aerial view of Djerba island coastline with white buildings, palm trees and turquoise Mediterranean sea",
          descKey: "act_island_desc" as const,
          pricing: [{ labelKey: "price_per_person" as const, label: null, price: "10€" }],
        },
        {
          id: "ksar-ghilane",
          nameKey: "act_ksar" as const,
          icon: "SunIcon",
          image: "https://img.rocket.new/generatedImages/rocket_gen_img_17c75a3ea-1773092861096.png",
          alt: "Stunning Sahara desert oasis at Ksar Ghilane with palm trees and golden sand dunes at sunset",
          descKey: "act_ksar_desc" as const,
          pricing: [
            { labelKey: "price_standard" as const, label: null, price: "60€/person" },
            { labelKey: "price_tent" as const, label: null, price: "80€/person" },
          ],
          badgeKey: "exp_recommended" as const,
        },
        {
          id: "tataouine",
          nameKey: "act_tataouine" as const,
          icon: "MapIcon",
          image: "https://images.unsplash.com/photo-1664346399421-971cb37cb450",
          alt: "Ancient Berber ksar fortress in Tataouine Tunisia with dramatic rocky landscape and blue sky",
          descKey: "act_tataouine_desc" as const,
          pricing: [{ labelKey: "price_per_person" as const, label: null, price: "50€" }],
        },
      ],
    },
  ];

  return (
    <section id="experiences" className="py-20 md:py-32 bg-background">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16 reveal will-animate">
          <span className="text-accent font-semibold text-sm uppercase tracking-wider">
            {t("exp_badge")}
          </span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-foreground mt-4 mb-6">
            {t("exp_title")}
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg">
            {t("exp_desc")}
          </p>
        </div>

        {/* Sections */}
        {sections.map((section) => (
          <div key={section.id} className="mb-20">
            {/* Section Title */}
            <div className="flex items-center gap-3 mb-8 reveal will-animate">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-muted">
                <Icon name={section.icon as Parameters<typeof Icon>[0]["name"]} size={20} className={section.color} />
              </div>
              <h3 className={`text-2xl font-bold ${section.color}`}>{t(section.titleKey)}</h3>
            </div>

            {/* Activities Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {section.activities.map((activity) => (
                <div
                  key={activity.id}
                  className="bg-card rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all group reveal will-animate"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <AppImage
                      src={activity.image}
                      alt={activity.alt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      width={600}
                      height={400}
                    />
                    {activity.badgeKey && (
                      <span className="absolute top-3 right-3 px-3 py-1 bg-accent text-white text-xs font-bold rounded-full">
                        {t(activity.badgeKey)}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Icon name={activity.icon as Parameters<typeof Icon>[0]["name"]} size={20} className="text-accent" />
                      <h4 className="text-lg font-bold text-foreground">{t(activity.nameKey)}</h4>
                    </div>
                    <p className="text-muted-foreground text-sm mb-4 leading-relaxed">
                      {t(activity.descKey)}
                    </p>

                    {/* Pricing */}
                    <div className="space-y-2 mb-4">
                      {activity.pricing.map((p, i) => (
                        <div key={i} className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">
                            {p.labelKey ? t(p.labelKey) : p.label}
                          </span>
                          <span className="font-bold text-foreground">{p.price}</span>
                        </div>
                      ))}
                    </div>

                    {/* Notes */}
                    {activity.notes && (
                      <div className="space-y-1 mb-4">
                        {activity.notes.map((noteKey, i) => (
                          <p key={i} className="text-xs text-muted-foreground flex items-center gap-1">
                            <Icon name="ClockIcon" size={12} />
                            {t(noteKey)}
                          </p>
                        ))}
                      </div>
                    )}

                    {/* Book Button */}
                    <a
                      href="/cart"
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-white rounded-xl font-semibold text-sm hover:bg-primary/90 transition-all hover:scale-[1.02]"
                    >
                      <Icon name="CalendarIcon" size={16} />
                      {t("exp_book_now")}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}